// Mock partner theme catalog. Mirrors the `theme` field shape the backend
// will eventually return from get-logo. Once the backend implements it,
// `getMockTheme` becomes obsolete — the response's `theme` object can be
// passed directly to `applyTheme`.
// Per-subdomain overrides. Add entries here once partners have their own
// palette decided. Anything not listed falls back to TEST_PALETTE during
// development so theming can be visually verified end-to-end.

// LA default palette — single source of truth for button/sidebar theming.
// CSS defaults in white-label-theme.css mirror these values.
export const DEFAULT_THEME = {
  primaryNavy: "#0d1830",
  primaryGreen: "#b2ea47",
  primaryGreen2: "#94C23C",
  hoverGreen: "rgb(148 194 60 / 98%)",
  darkBackground: "#19181c",
  lightGreenOverlay: "rgba(178, 234, 71, 0.102)",
  lightBgColor: "rgba(178, 234, 71, 0.102)",
  warningAccent: "#1fa878ba",
  highlightYellow: "#fff300bd",
  deepTeal: "#093330",
  // Partner-catalog aliases (navy / green)
  primary: "#0d1830",
  secondary: "#b2ea47",
  secondaryGreen: "#94C23C",
  light: "#19181c",
};

const PARTNER_THEMES = {
  softude: {
    primary: "#223c35eb",
    secondary: "#1fa878ba",
    lightBgColor: "#ffefd1",
    light: "#ffff",
    secondaryGreen: "#C8102E",
    highlightYellow: "#C8102E",
    deepTeal: "#2A0E13",
  },
};

const TEST_PALETTE = {
  primary: "#223c35eb",
  secondary: "#1fa878ba",
  lightBgColor: "#ffefd1",
  light: "#ffff",
  secondaryGreen: "#C8102E",
  highlightYellow: "#C8102E",
  deepTeal: "#2A0E13",
};

export const getMockTheme = (subdomain) =>
  PARTNER_THEMES[subdomain] || TEST_PALETTE;

// Partner API themes often include only primary/secondary. Fill any missing
// keys from the subdomain mock palette so tokens like lightBgColor follow the
// same partner defaults as the sidebar colors.
export const mergePartnerTheme = (theme, subdomain) => ({
  ...getMockTheme(subdomain),
  ...(theme && typeof theme === "object" ? theme : {}),
});

// Writes a partner theme to :root as CSS custom properties so every
// `var(--la-*)` reference in stylesheets and inline styles re-resolves.
// Each field is optional — a missing key falls back to the default in
// white-label-theme.css.
//
// `primary` / `secondary` remain supported for partner catalogs and also
// update the navy/green named tokens plus the legacy aliases.
export const applyTheme = (theme) => {
  if (!theme || typeof theme !== "object") return;
  const root = document.documentElement;

  const navy = theme.primaryNavy || theme.primary;
  const green = theme.primaryGreen || theme.secondary;
  const lightBgColor = theme.lightBgColor ?? DEFAULT_THEME.lightBgColor;
  const highlightYellow = theme.highlightYellow ?? DEFAULT_THEME.highlightYellow;
  const deepTeal = theme.deepTeal ?? DEFAULT_THEME.deepTeal;

  if (navy) {
    root.style.setProperty("--la-primary-navy", navy);
    root.style.setProperty("--la-primary", navy);
  }
  if (green) {
    root.style.setProperty("--la-primary-green", green);
    root.style.setProperty("--la-secondary", green);
  }
  if (theme.hoverGreen) {
    root.style.setProperty("--la-hover-green", theme.hoverGreen);
  }
  if (theme.darkBackground) {
    root.style.setProperty("--la-dark-background", theme.darkBackground);
  }
  if (theme.lightGreenOverlay) {
    root.style.setProperty("--la-light-green-overlay", theme.lightGreenOverlay);
  }
  if (theme.warningAccent) {
    root.style.setProperty("--la-warning-accent", theme.warningAccent);
  }
  if (lightBgColor) {
    root.style.setProperty("--la-light-bg-color", lightBgColor);
  }
  if (theme.light) {
    root.style.setProperty("--la-light", theme.light);
  }
   if (theme.secondaryGreen) {
    root.style.setProperty("--la-secondary-2", theme.secondaryGreen);
  }
  if (theme.highlightYellow) {
    root.style.setProperty("--la-highlight-yellow", theme.highlightYellow);
  }
  if (theme.deepTeal) {
    root.style.setProperty("--la-deep-teal", theme.deepTeal);
  }
};
